import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  InputText,
  InputSize,
  SelectSize,
  Checkbox,
  Textarea,
  Autocomplete,
  Select,
  DateInput,
  DateInputSize,
  SelectType,
  ButtonSecondary,
  IconExpand,
  ButtonSize,
  IconLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import groupBy from 'lodash.groupby';
import InfoRow from '../info-row';
import CountriesOnInterests from './step-one-countries-on-interests';
import SicCodes from './step-one-sic-codes';
import RequiredCurrencies from './step-one-required-currencies';
import {
  AccountsCurrenciesFormValues,
  NewApplicationStatus,
} from '../../../hoc/new-application/types';
import { CorporateApplicationStepOneTypes } from '../types';
import StepPartBlock from './parts/step-part-block';
import StepOneBlockWrapper from './parts/step-one-block-wrapper';
import PepQuestionnarie from './parts/pep-questionnarie';
import GroupOfCompanies from './parts/group-of-companies';
import BusinessActivities from './parts/busniess-activites';
import CustomerWealth from './parts/customer-wealth';
import AccountCurrency from './parts/account-currency';
import AccountFlow from './parts/account-flow';
import ExpectedFundsOrigins from './parts/expected-funds-origins';

import cn from '../new-application.module.css';

const NewApplicationCorporateInfoStepOne = ({
  control,
  errors,
  countries,
  fields,
  sicCodes,
  countriesOfInterest,
  setValue,
  status,
  isEditable,
  applicationScheme,
  clearErrors,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  questionnarieFields,
  currencies,
  requiredCurrencies,
  regulatedCompanyScheme,
  relationshipToTheCustomerDictionary,
  sourceOfWealth,
  baseCurrency,
  busniessActivities,
  customerMainCleints,
  salesChannels,
  questionnarieSetValue,
  customerSourceOfWealth,
  customerSourceOfFunds,
  companyLegalName,
  annualIncomes,
  totalIncomeError,
  setTotalIncomeError,
  typeOfAccounts,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  accountsFlowsValues,
  setAccountsFlowsValues,
  currenciesList,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
}: CorporateApplicationStepOneTypes) => {
  const { deliveryChannelsCorporate } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [sicCodesList, setSicCodesList] = useState<
    { name: string; id: string }[]
  >([{ name: '', id: '' }]);
  const [countriesOfInterestList, setCountriesOfInterestList] = useState<
    { name: string; id: string }[]
  >([{ name: '', id: '' }]);
  const [requiredCurrenciesList, setRequiredCurrenciesList] = useState<
    { name: string; id: string }[]
  >([{ name: '', id: '' }]);

  const groupedAccountsFlows = groupBy(
    accountsFlowsValues,
    (match) => match.currencyId,
  );

  const incomingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'INCOMING',
  );
  const outgoingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'OUTGOING',
  );

  useEffect(() => {
    if (sicCodes?.length) {
      setSicCodesList(sicCodes.map((item) => ({ name: item, id: item })));
    }
  }, [sicCodes]);

  useEffect(() => {
    if (countriesOfInterest?.length) {
      setCountriesOfInterestList(
        countriesOfInterest.map((item) => ({ name: item, id: item })),
      );
    }
  }, [countriesOfInterest]);

  useEffect(() => {
    if (requiredCurrencies?.length) {
      setRequiredCurrenciesList(
        requiredCurrencies.map((item) => ({ name: item, id: item })),
      );
    }
  }, [requiredCurrencies]);

  let countriesOfInterestVisible = false;
  let deliveryChannelsVisible = false;

  if (
    !applicationScheme ||
    (applicationScheme && applicationScheme.countriesOfInterestActive) ||
    (applicationScheme && applicationScheme?.countriesOfInterestActive === null)
  ) {
    countriesOfInterestVisible = true;
  }

  if (
    !applicationScheme ||
    (applicationScheme && applicationScheme.deliveryChannelVisible)
  ) {
    deliveryChannelsVisible = true;
  }

  return (
    <div className={cn.stepBlockWrapper}>
      <StepOneBlockWrapper
        title="Company profile"
        regulatedCompanyScheme={regulatedCompanyScheme}
      >
        <div className={cn.stepBlockContent}>
          <StepPartBlock title="Country and Name">
            <>
              <InfoRow
                title="Country of incorporation of the company"
                id="countryOfIncorporationId"
                lessPadding
              >
                <Controller
                  control={control}
                  name="countryOfIncorporationId"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      name="countryOfIncorporationId"
                      placeholder="Select country..."
                      list={countries}
                      value={value}
                      error={errors?.countryOfIncorporationId?.message}
                      className={`${cn.newApplicationAutocomplete} ${
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.newApplicationAutocompleteDisabled
                          : ''
                      }`}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);

                        clearErrors(['countryOfIncorporationId']);
                      }}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      borderless
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Company name" id="companyName" lessPadding>
                <Controller
                  control={control}
                  name="companyName"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="companyName"
                      placeholder="Enter company name..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);

                        clearErrors(['companyName']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      error={errors?.companyName?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              {regulatedCompanyScheme && (
                <InfoRow
                  title="Trading name (optional)"
                  id="tradingName"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="tradingName"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter trading name..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['tradingName']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.tradingName?.message}
                      />
                    )}
                  />
                </InfoRow>
              )}
            </>
          </StepPartBlock>

          <StepPartBlock title="Registered address">
            <>
              <InfoRow title="Country" id="registeredCountryId" lessPadding>
                <Controller
                  control={control}
                  name="registeredCountryId"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      name="registeredCountryId"
                      placeholder="Select country..."
                      list={countries}
                      value={value}
                      size={SelectSize.SMALL}
                      className={`${cn.newApplicationAutocomplete} ${
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.newApplicationAutocompleteDisabled
                          : ''
                      }`}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['registeredCountryId']);
                      }}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      error={errors.registeredCountryId?.message}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="City" id="registeredCity" lessPadding>
                <Controller
                  control={control}
                  name="registeredCity"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="registeredCity"
                      placeholder="Enter city..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['registeredCity']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={errors?.registeredCity?.message}
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Postal code"
                id="registeredPostalCode"
                lessPadding
              >
                <Controller
                  control={control}
                  name="registeredPostalCode"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="registeredPostalCode"
                      placeholder="Enter postal code..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['registeredPostalCode']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={errors?.registeredPostalCode?.message}
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Address 1" id="registeredAddress1" lessPadding>
                <Controller
                  control={control}
                  name="registeredAddress1"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="registeredAddress1"
                      placeholder="Enter address 1..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['registeredAddress1']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={errors?.registeredAddress1?.message}
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Address 2" id="registeredAddress2" lessPadding>
                <Controller
                  control={control}
                  name="registeredAddress2"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="registeredAddress2"
                      placeholder="Enter address 2..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['registeredAddress2']);
                      }}
                      size={InputSize.SMALL}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      className="borderless"
                      error={errors.registeredAddress2?.message}
                    />
                  )}
                />
              </InfoRow>
            </>
          </StepPartBlock>
          <StepPartBlock title="Business address">
            <>
              <div
                className={`${cn.addressCheckboxWrapper} ${cn.stepOneCheckboxWrapper} ${cn.cddBlockContentCheckbox}`}
              >
                <Controller
                  control={control}
                  name="businessAddressSameAsRegisteredAddress"
                  defaultValue={false}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      name="businessAddressSameAsRegisteredAddress"
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      title="Coincides with the registered address"
                      labelClassName={cn.addressCheckbox}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                    />
                  )}
                />
              </div>
              {!fields?.businessAddressSameAsRegisteredAddress && (
                <>
                  <InfoRow title="Country" id="businessCountryId" lessPadding>
                    <Controller
                      control={control}
                      name="businessCountryId"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          name="businessCountryId"
                          placeholder="Select country..."
                          list={countries}
                          value={value}
                          size={SelectSize.SMALL}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['businessCountryId']);
                          }}
                          className={`${cn.newApplicationAutocomplete} ${
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? cn.newApplicationAutocompleteDisabled
                              : ''
                          }`}
                          disabled={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          borderless
                          error={errors.businessCountryId?.message}
                          fullWidth
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="City" id="businessCity" lessPadding>
                    <Controller
                      control={control}
                      name="businessCity"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="businessCity"
                          placeholder="Enter city..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['businessCity']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.businessCity?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Postal code"
                    id="businessPostalCode"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="businessPostalCode"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="businessPostalCode"
                          placeholder="Enter postal code..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['businessPostalCode']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.businessPostalCode?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="Address 1" id="businessAddress1" lessPadding>
                    <Controller
                      control={control}
                      name="businessAddress1"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="businessAddress1"
                          placeholder="Enter address 1..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['businessAddress1']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.businessAddress1?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="Address 2" id="businessAddress2" lessPadding>
                    <Controller
                      control={control}
                      name="businessAddress2"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="businessAddress2"
                          placeholder="Enter address 2..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['businessAddress2']);
                          }}
                          size={InputSize.SMALL}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          className="borderless"
                          error={errors.businessAddress2?.message}
                        />
                      )}
                    />
                  </InfoRow>
                </>
              )}
            </>
          </StepPartBlock>
          <StepPartBlock title="Correspondence address">
            <>
              <div
                className={`${cn.addressCheckboxWrapper} ${cn.stepOneCheckboxWrapper} ${cn.cddBlockContentCheckbox}`}
              >
                <Controller
                  control={control}
                  name="correspondenceAddressSameAsRegisteredAddress"
                  defaultValue={false}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      name="correspondenceAddressSameAsRegisteredAddress"
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      title="Coincides with the registered address"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      labelClassName={cn.addressCheckbox}
                    />
                  )}
                />
              </div>
              {!fields?.correspondenceAddressSameAsRegisteredAddress && (
                <>
                  <InfoRow
                    title="Country"
                    id="correspondenceCountryId"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="correspondenceCountryId"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          name="correspondenceCountryId"
                          placeholder="Select country..."
                          list={countries}
                          value={value}
                          size={SelectSize.SMALL}
                          className={`${cn.newApplicationAutocomplete} ${
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? cn.newApplicationAutocompleteDisabled
                              : ''
                          }`}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['correspondenceCountryId']);
                          }}
                          error={errors.correspondenceCountryId?.message}
                          borderless
                          fullWidth
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="City" id="correspondenceCity" lessPadding>
                    <Controller
                      control={control}
                      name="correspondenceCity"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="correspondenceCity"
                          placeholder="Enter city..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['correspondenceCity']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.correspondenceCity?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Postal code"
                    id="correspondencePostalCode"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="correspondencePostalCode"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="correspondencePostalCode"
                          placeholder="Enter postal code..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['correspondencePostalCode']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.correspondencePostalCode?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Address 1"
                    id="correspondenceAddress1"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="correspondenceAddress1"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="correspondenceAddress1"
                          placeholder="Enter address 1..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['correspondenceAddress1']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={errors?.correspondenceAddress1?.message}
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Address 2"
                    id="correspondenceAddress2"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="correspondenceAddress2"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <InputText
                          name="correspondenceAddress2"
                          placeholder="Enter address 2..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['correspondenceAddress2']);
                          }}
                          size={InputSize.SMALL}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          className="borderless"
                          error={errors.correspondenceAddress2?.message}
                        />
                      )}
                    />
                  </InfoRow>
                </>
              )}
            </>
          </StepPartBlock>
          {regulatedCompanyScheme && (
            <StepPartBlock title="Contact details">
              <>
                <InfoRow
                  title="Contact person name"
                  id="contactPersonName"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="contactPersonName"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter name..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['contactPersonName']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.contactPersonName?.message}
                      />
                    )}
                  />
                </InfoRow>
                <InfoRow title="Phone number" id="phoneNumber" lessPadding>
                  <Controller
                    control={questionnarieControl}
                    name="phoneNumber"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter number..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['phoneNumber']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.phoneNumber?.message}
                      />
                    )}
                  />
                </InfoRow>
                <InfoRow
                  title="Fax number (optional)"
                  id="faxNumber"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="faxNumber"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter number..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['faxNumber']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.faxNumber?.message}
                      />
                    )}
                  />
                </InfoRow>
                <InfoRow title="Contact email" id="contactEmail" lessPadding>
                  <Controller
                    control={questionnarieControl}
                    name="contactEmail"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter email..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['contactEmail']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.contactEmail?.message}
                      />
                    )}
                  />
                </InfoRow>
              </>
            </StepPartBlock>
          )}
          {applicationScheme &&
            (applicationScheme?.accountantDetailsActive ||
              applicationScheme?.auditedByExternalComplianceActive ||
              applicationScheme?.changesInManagementOrOwnershipActive ||
              applicationScheme?.complianceConsultantDetailsActive ||
              applicationScheme?.fcaNumberActive ||
              applicationScheme?.hmrcMlrNumberActive ||
              applicationScheme?.hmrcRegistrationExpiryDateActive ||
              applicationScheme?.icoNumberActive ||
              applicationScheme?.itProviderDetailsActive ||
              applicationScheme?.mlroDetailsActive ||
              applicationScheme?.numberOfAgentsActive ||
              applicationScheme?.numberOfBranchesActive ||
              applicationScheme?.onboardCorporateClientsActive ||
              applicationScheme?.onboardMsbActive ||
              applicationScheme?.paymentsForCharitiesActive ||
              applicationScheme?.payoutPartnerDetailsActive ||
              applicationScheme?.regulatoryInvestigationActive ||
              applicationScheme?.screeningActive ||
              applicationScheme?.transactionMonitoringActive ||
              applicationScheme?.typeOfBusinessLicenceActive ||
              applicationScheme?.visitedByAuthoritiesActive ||
              applicationScheme?.workingWithAnyOtherIPSPActive) && (
              <>
                {(applicationScheme?.typeOfBusinessLicenceActive ||
                  applicationScheme?.hmrcMlrNumberActive ||
                  applicationScheme?.hmrcRegistrationExpiryDateActive ||
                  applicationScheme?.fcaNumberActive ||
                  applicationScheme?.icoNumberActive) && (
                  <StepPartBlock title="Licenses and registration">
                    <>
                      {applicationScheme?.typeOfBusinessLicenceActive && (
                        <InfoRow
                          title="Type of business licence? (API, SPI, EMI, OTHER)"
                          id="typeOfBusinessLicence"
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="typeOfBusinessLicence"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="typeOfBusinessLicence"
                                placeholder="Enter type..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'typeOfBusinessLicence',
                                  ]);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={
                                  questionnarieErrors?.typeOfBusinessLicence
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.hmrcMlrNumberActive && (
                        <InfoRow
                          title="HMRC MLR number"
                          id="hmrcMlrNumber"
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="hmrcMlrNumber"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="hmrcMlrNumber"
                                placeholder="Enter number..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['hmrcMlrNumber']);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={
                                  questionnarieErrors?.hmrcMlrNumber?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.hmrcRegistrationExpiryDateActive && (
                        <InfoRow
                          title="HMRC expiry date"
                          id="hmrcRegistrationExpiryDate"
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="hmrcRegistrationExpiryDate"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <DateInput
                                placeholder="Select expiry date..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hmrcRegistrationExpiryDate',
                                  ]);
                                }}
                                size={DateInputSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hmrcRegistrationExpiryDate?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                withoutCustomSelector
                                borderless
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.fcaNumberActive && (
                        <InfoRow title="FCA number" id="fcaNumber" lessPadding>
                          <Controller
                            control={questionnarieControl}
                            name="fcaNumber"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="fcaNumber"
                                placeholder="Enter FCA number..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['fcaNumber']);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={questionnarieErrors?.fcaNumber?.message}
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.icoNumberActive && (
                        <InfoRow title="ICO number" id="icoNumber" lessPadding>
                          <Controller
                            control={questionnarieControl}
                            name="icoNumber"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="icoNumber"
                                placeholder="Enter ICO number..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['icoNumber']);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={questionnarieErrors?.icoNumber?.message}
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                    </>
                  </StepPartBlock>
                )}
                {(applicationScheme?.numberOfBranchesActive ||
                  applicationScheme?.numberOfAgentsActive) && (
                  <StepPartBlock title="Business Structure">
                    <>
                      {applicationScheme?.numberOfBranchesActive && (
                        <InfoRow
                          title="Number of branches"
                          id="numberOfBranches"
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="numberOfBranches"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="numberOfBranches"
                                placeholder="Enter number of branches..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'numberOfBranches',
                                  ]);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={
                                  questionnarieErrors?.numberOfBranches?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.numberOfAgentsActive && (
                        <InfoRow
                          title="Number of agents"
                          id="numberOfAgents"
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="numberOfAgents"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <InputText
                                name="numberOfAgents"
                                placeholder="Enter number of agents..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['numberOfAgents']);
                                }}
                                size={InputSize.SMALL}
                                className="borderless"
                                error={
                                  questionnarieErrors?.numberOfAgents?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                    </>
                  </StepPartBlock>
                )}
                {(applicationScheme?.mlroDetailsActive ||
                  applicationScheme?.complianceConsultantDetailsActive ||
                  applicationScheme?.accountantDetailsActive ||
                  applicationScheme?.itProviderDetailsActive ||
                  applicationScheme?.payoutPartnerDetailsActive) && (
                  <StepPartBlock title="Key contacts">
                    <>
                      {applicationScheme?.mlroDetailsActive && (
                        <InfoRow
                          title="MLRO details"
                          id="mlroDetails"
                          className={cn.noFlex}
                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="mlroDetails"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Textarea
                                name="mlroDetails"
                                placeholder="Enter details..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['mlroDetails']);
                                }}
                                className="borderless"
                                error={
                                  questionnarieErrors?.mlroDetails?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.complianceConsultantDetailsActive && (
                        <InfoRow
                          title="Compliance consultant details"
                          id="complianceConsultantDetails"
                          className={cn.noFlex}

                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="complianceConsultantDetails"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Textarea
                                name="complianceConsultantDetails"
                                placeholder="Enter details..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'complianceConsultantDetails',
                                  ]);
                                }}
                                className="borderless"
                                error={
                                  questionnarieErrors
                                    ?.complianceConsultantDetails?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.accountantDetailsActive && (
                        <InfoRow
                          title="Accountant details"
                          id="accountantDetails"
                          className={cn.noFlex}

                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="accountantDetails"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Textarea
                                name="accountantDetails"
                                placeholder="Enter details..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'accountantDetails',
                                  ]);
                                }}
                                className="borderless"
                                error={
                                  questionnarieErrors?.accountantDetails
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.itProviderDetailsActive && (
                        <InfoRow
                          title="IT provider details"
                          id="itProviderDetails"
                          className={cn.noFlex}

                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="itProviderDetails"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Textarea
                                name="itProviderDetails"
                                placeholder="Enter details..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'itProviderDetails',
                                  ]);
                                }}
                                className="borderless"
                                error={
                                  questionnarieErrors?.itProviderDetails
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                      {applicationScheme?.payoutPartnerDetailsActive && (
                        <InfoRow
                          title="Pay out partner details"
                          id="payoutPartnerDetails"
                          className={cn.noFlex}

                          lessPadding
                        >
                          <Controller
                            control={questionnarieControl}
                            name="payoutPartnerDetails"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Textarea
                                name="payoutPartnerDetails"
                                placeholder="Enter details..."
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'payoutPartnerDetails',
                                  ]);
                                }}
                                className="borderless"
                                error={
                                  questionnarieErrors?.payoutPartnerDetails
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                              />
                            )}
                          />
                        </InfoRow>
                      )}
                    </>
                  </StepPartBlock>
                )}
                <StepPartBlock title="Checklist of questions">
                  <>
                    {applicationScheme?.transactionMonitoringActive && (
                      <InfoRow
                        title="Do you carry out transaction monitoring?"
                        id="hasTransactionMonitoring"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasTransactionMonitoring"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasTransactionMonitoring"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hasTransactionMonitoring',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors?.hasTransactionMonitoring
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasTransactionMonitoring ===
                            'yes' && (
                            <InfoRow
                              title="Is your transaction monitoring"
                              id="transactionMonitoringKind"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="transactionMonitoringKind"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    name="transactionMonitoringKind"
                                    placeholder="Select option"
                                    list={[
                                      {
                                        name: 'Automated',
                                        value: 'AUTO',
                                      },
                                      {
                                        name: 'Manual',
                                        value: 'MANUAL',
                                      },
                                    ]}
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'transactionMonitoringKind',
                                      ]);
                                    }}
                                    className="borderless"
                                    size={SelectSize.SMALL}
                                    error={
                                      questionnarieErrors
                                        ?.transactionMonitoringKind?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                    fullWidth
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.screeningActive && (
                      <InfoRow
                        title="Do you carry out sanction screening, adverse media screening, and PEP screening?"
                        id="hasScreening"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasScreening"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasScreening"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors(['hasScreening']);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors?.hasScreening?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasScreening === 'yes' && (
                            <InfoRow
                              title="Is your screening"
                              id="screeningKind"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="screeningKind"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    name="screeningKind"
                                    placeholder="Select option"
                                    list={[
                                      {
                                        name: 'Automated',
                                        value: 'AUTO',
                                      },
                                      {
                                        name: 'Manual',
                                        value: 'MANUAL',
                                      },
                                    ]}
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'screeningKind',
                                      ]);
                                    }}
                                    className="borderless"
                                    size={SelectSize.SMALL}
                                    error={
                                      questionnarieErrors?.screeningKind
                                        ?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                    fullWidth
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.regulatoryInvestigationActive && (
                      <InfoRow
                        title="Have you been under any regulatory investigation in the last 24 months?"
                        id="hasRegulatoryInvestigation"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasRegulatoryInvestigation"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasRegulatoryInvestigation"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hasRegulatoryInvestigation',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hasRegulatoryInvestigation?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasRegulatoryInvestigation ===
                            'yes' && (
                            <InfoRow
                              title="Provide details"
                              id="regulatoryInvestigationDetails"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="regulatoryInvestigationDetails"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Textarea
                                    name="regulatoryInvestigationDetails"
                                    placeholder="Enter details..."
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'regulatoryInvestigationDetails',
                                      ]);
                                    }}
                                    className={cn.provideDetailsTextarea}
                                    error={
                                      questionnarieErrors
                                        ?.regulatoryInvestigationDetails
                                        ?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.visitedByAuthoritiesActive && (
                      <InfoRow
                        title="Have you been visited by any of the following authorities in the last 24 months?"
                        id="visitedByAuthorities"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="visitedByAuthorities"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="visitedByAuthorities"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'HMRC',
                                    value: 'HMRC',
                                  },
                                  {
                                    name: 'FCA',
                                    value: 'FCA',
                                  },
                                  {
                                    name: 'NCA',
                                    value: 'NCA',
                                  },
                                  {
                                    name: 'Metropolitan Police',
                                    value: 'METROPOLITAN_POLICE',
                                  },
                                  {
                                    name: 'No',
                                    value: 'NO',
                                  },
                                ]}
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'visitedByAuthorities',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hasRegulatoryInvestigation?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.visitedByAuthorities !==
                            'NO' && (
                            <InfoRow
                              title="Provide details"
                              id="visitedByAuthoritiesDetails"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="visitedByAuthoritiesDetails"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Textarea
                                    name="visitedByAuthoritiesDetails"
                                    placeholder="Enter details..."
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'visitedByAuthoritiesDetails',
                                      ]);
                                    }}
                                    className={cn.provideDetailsTextarea}
                                    error={
                                      questionnarieErrors
                                        ?.visitedByAuthoritiesDetails?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.onboardCorporateClientsActive && (
                      <InfoRow
                        title="Do you onboard corporate clients?"
                        id="hasOnboardCorporateClients"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="hasOnboardCorporateClients"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="hasOnboardCorporateClients"
                              placeholder="Select «Yes» or «No»"
                              list={[
                                {
                                  name: 'Yes',
                                  value: 'yes',
                                },
                                {
                                  name: 'No',
                                  value: 'no',
                                },
                              ]}
                              value={value as string}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors([
                                  'hasOnboardCorporateClients',
                                ]);
                              }}
                              className="borderless"
                              size={SelectSize.SMALL}
                              error={
                                questionnarieErrors?.hasOnboardCorporateClients
                                  ?.message
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              fullWidth
                            />
                          )}
                        />
                      </InfoRow>
                    )}
                    {applicationScheme?.onboardMsbActive && (
                      <InfoRow
                        title="Do you onboard Money Service Businesses (MSBs)?"
                        id="hasOnboardMsb"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="hasOnboardMsb"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="hasOnboardMsb"
                              placeholder="Select «Yes» or «No»"
                              list={[
                                {
                                  name: 'Yes',
                                  value: 'yes',
                                },
                                {
                                  name: 'No',
                                  value: 'no',
                                },
                              ]}
                              value={value as string}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors(['hasOnboardMsb']);
                              }}
                              className="borderless"
                              size={SelectSize.SMALL}
                              error={
                                questionnarieErrors?.hasOnboardMsb?.message
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              fullWidth
                            />
                          )}
                        />
                      </InfoRow>
                    )}
                    {applicationScheme?.paymentsForCharitiesActive && (
                      <InfoRow
                        title="Do you make payments for charities?"
                        id="hasPaymentsForCharities"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="hasPaymentsForCharities"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="hasPaymentsForCharities"
                              placeholder="Select «Yes» or «No»"
                              list={[
                                {
                                  name: 'Yes',
                                  value: 'yes',
                                },
                                {
                                  name: 'No',
                                  value: 'no',
                                },
                              ]}
                              value={value as string}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors([
                                  'hasPaymentsForCharities',
                                ]);
                              }}
                              className="borderless"
                              size={SelectSize.SMALL}
                              error={
                                questionnarieErrors?.hasPaymentsForCharities
                                  ?.message
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              fullWidth
                            />
                          )}
                        />
                      </InfoRow>
                    )}
                    {applicationScheme?.changesInManagementOrOwnershipActive && (
                      <InfoRow
                        title="Is the company undergoing any changes in management or ownership?"
                        id="hasChangesInManagementOrOwnership"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasChangesInManagementOrOwnership"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasChangesInManagementOrOwnership"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hasChangesInManagementOrOwnership',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hasChangesInManagementOrOwnership?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasChangesInManagementOrOwnership ===
                            'yes' && (
                            <InfoRow
                              title="Provide details"
                              id="changesInManagementOrOwnershipDetails"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="changesInManagementOrOwnershipDetails"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Textarea
                                    name="changesInManagementOrOwnershipDetails"
                                    placeholder="Enter details..."
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'changesInManagementOrOwnershipDetails',
                                      ]);
                                    }}
                                    className={cn.provideDetailsTextarea}
                                    error={
                                      questionnarieErrors
                                        ?.changesInManagementOrOwnershipDetails
                                        ?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.workingWithAnyOtherIPSPActive && (
                      <InfoRow
                        title="Are you currently working with any other IPSP (Intermediary Payment Service Provider)?"
                        id="hasWorkingWithAnyOtherIpsp"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasWorkingWithAnyOtherIpsp"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasWorkingWithAnyOtherIpsp"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hasWorkingWithAnyOtherIpsp',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hasWorkingWithAnyOtherIpsp?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasWorkingWithAnyOtherIpsp ===
                            'yes' && (
                            <InfoRow
                              title="Provide details"
                              id="workingWithAnyOtherIpspDetails"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="workingWithAnyOtherIpspDetails"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Textarea
                                    name="workingWithAnyOtherIpspDetails"
                                    placeholder="Enter details..."
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'workingWithAnyOtherIpspDetails',
                                      ]);
                                    }}
                                    className={cn.provideDetailsTextarea}
                                    error={
                                      questionnarieErrors
                                        ?.workingWithAnyOtherIpspDetails
                                        ?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                    {applicationScheme?.auditedByExternalComplianceActive && (
                      <InfoRow
                        title="Has the institution been audited by external compliance auditors in the past 12 months?"
                        id="hasAuditedByExternalCompliance"
                        lessPadding
                      >
                        <>
                          <Controller
                            control={questionnarieControl}
                            name="hasAuditedByExternalCompliance"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="hasAuditedByExternalCompliance"
                                placeholder="Select «Yes» or «No»"
                                list={[
                                  {
                                    name: 'Yes',
                                    value: 'yes',
                                  },
                                  {
                                    name: 'No',
                                    value: 'no',
                                  },
                                ]}
                                value={value as string}
                                onChange={(e) => {
                                  onChange(e);
                                  questionnarieClearErrors([
                                    'hasAuditedByExternalCompliance',
                                  ]);
                                }}
                                className="borderless"
                                size={SelectSize.SMALL}
                                error={
                                  questionnarieErrors
                                    ?.hasAuditedByExternalCompliance?.message
                                }
                                tabIndex={
                                  !isEditable &&
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                fullWidth
                              />
                            )}
                          />
                          {questionnarieFields?.hasAuditedByExternalCompliance ===
                            'yes' && (
                            <InfoRow
                              title="Provide details"
                              id="auditedByExternalComplianceDetails"
                              className={cn.withoutPaddingInfoRow}
                            >
                              <Controller
                                control={questionnarieControl}
                                name="auditedByExternalComplianceDetails"
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                  <Textarea
                                    name="auditedByExternalComplianceDetails"
                                    placeholder="Enter details..."
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e);
                                      questionnarieClearErrors([
                                        'auditedByExternalComplianceDetails',
                                      ]);
                                    }}
                                    className={cn.provideDetailsTextarea}
                                    error={
                                      questionnarieErrors
                                        ?.auditedByExternalComplianceDetails
                                        ?.message
                                    }
                                    tabIndex={
                                      !isEditable &&
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                        ? -1
                                        : 0
                                    }
                                    disabled={
                                      !isEditable ||
                                      Boolean(
                                        status ===
                                          NewApplicationStatus.ACCEPTED ||
                                        status ===
                                          NewApplicationStatus.DECLINED,
                                      )
                                    }
                                  />
                                )}
                              />
                            </InfoRow>
                          )}
                        </>
                      </InfoRow>
                    )}
                  </>
                </StepPartBlock>
              </>
            )}
          <StepPartBlock title="Registration information">
            <>
              <InfoRow
                title="Date of incorporation"
                id="dateOfIncorporation"
                lessPadding
              >
                <Controller
                  control={control}
                  name="dateOfIncorporation"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    let className;

                    if (
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                    ) {
                      className = cn.dateInputDisabled;

                      if (value) {
                        className = `${cn.dateInputDisabled} ${
                          value ? cn.dateInputDisabledWithValue : ''
                        }`;
                      }
                    }
                    return (
                      <DateInput
                        value={value}
                        placeholder="Enter date of incorporation"
                        onChange={(date) => {
                          onChange(date.toISOString());
                          clearErrors(['dateOfIncorporation']);
                        }}
                        className={className}
                        size={DateInputSize.SMALL}
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={errors.dateOfIncorporation?.message}
                        disabledAfter={new Date()}
                        borderless
                        withoutCustomSelector
                      />
                    );
                  }}
                />
              </InfoRow>
              <InfoRow title="Company Number" id="companyNumber" lessPadding>
                <Controller
                  control={control}
                  name="companyNumber"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="companyNumber"
                      placeholder="Enter company number..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['companyNumber']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={errors?.companyNumber?.message}
                    />
                  )}
                />
              </InfoRow>
              {regulatedCompanyScheme && (
                <InfoRow
                  title="Legal Entity Identifier Code (optional)"
                  id="legalEntityIdCode"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="legalEntityIdCode"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter code..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['legalEntityIdCode']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.legalEntityIdCode?.message}
                      />
                    )}
                  />
                </InfoRow>
              )}
              {sicCodesList?.map((item, index) => (
                <SicCodes
                  // eslint-disable-next-line react/no-array-index-key
                  key={`sic-${item.id}-${index}`}
                  value={item}
                  setValue={setValue}
                  values={sicCodesList}
                  setValuesList={setSicCodesList}
                  index={index}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  error={errors.sicCodes?.message}
                  clearErrors={clearErrors}
                />
              ))}
              {isEditable &&
                status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <button
                    type="button"
                    className={cn.stepOneValueButton}
                    onClick={() => {
                      const nextSicCodes = (
                        [] as { id: string; name: string }[]
                      ).concat(sicCodesList);

                      nextSicCodes.push({ id: '', name: '' });

                      setSicCodesList(nextSicCodes);
                    }}
                    tabIndex={-1}
                  >
                    <span>+</span> add one more SIC
                  </button>
                )}
              <div className={cn.separator} />
              {regulatedCompanyScheme && (
                <InfoRow
                  title="Do the Customer’s business activities require a license / permit / registration?"
                  id="requiresBusinessLicense"
                  lessPadding
                >
                  <Controller
                    control={control}
                    name="requiresBusinessLicense"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="requiresBusinessLicense"
                        placeholder="Select an option..."
                        list={[
                          {
                            name: 'Yes',
                            value: 'yes',
                          },
                          {
                            name: 'No',
                            value: 'no',
                          },
                        ]}
                        value={value as string}
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['requiresBusinessLicense']);
                        }}
                        selectWrapperClassName={
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? cn.selectDisabled
                            : ''
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={errors?.requiresBusinessLicense?.message}
                        fullWidth
                        borderless
                      />
                    )}
                  />
                </InfoRow>
              )}
              {((regulatedCompanyScheme &&
                fields?.requiresBusinessLicense === 'yes') ||
                !regulatedCompanyScheme) && (
                <InfoRow
                  title={`Business license ${regulatedCompanyScheme ? 'number' : '(optional)'}`}
                  id="businessLicense"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={control}
                    name="businessLicense"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Textarea
                        name="businessLicense"
                        placeholder="Enter license..."
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['businessLicense']);
                        }}
                        value={value}
                        className={`borderless ${cn.stepOneTextarea}`}
                        error={errors?.businessLicense?.message}
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              <InfoRow title="Link to the website" id="websiteLink" lessPadding>
                <Controller
                  control={control}
                  name="websiteLink"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="websiteLink"
                      placeholder="Enter link..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['websiteLink']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      error={errors?.websiteLink?.message}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                    />
                  )}
                />
              </InfoRow>
              {countriesOfInterestVisible && (
                <>
                  {countriesOfInterestList?.map((item, index) => (
                    <CountriesOnInterests
                      title={
                        applicationScheme &&
                        applicationScheme?.countriesOfInterestTitle
                          ? applicationScheme?.countriesOfInterestTitle
                          : 'Countries of interest'
                      }
                      // eslint-disable-next-line react/no-array-index-key
                      key={`countriesOfInterestIds-${item.name}-${index}`}
                      value={item}
                      setValue={setValue}
                      values={countriesOfInterestList}
                      setValuesList={setCountriesOfInterestList}
                      list={countries}
                      index={index}
                      clearErrors={clearErrors}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      error={
                        errors.countriesOfInterestIds?.message ||
                        errors.countriesOfInterest?.message
                      }
                    />
                  ))}
                  {isEditable &&
                    status !== NewApplicationStatus.ACCEPTED &&
                    status !== NewApplicationStatus.DECLINED && (
                      <button
                        type="button"
                        className={cn.stepOneValueButton}
                        onClick={() => {
                          const nextCountriesOfInterest = (
                            [] as { id: string; name: string }[]
                          ).concat(countriesOfInterestList);

                          nextCountriesOfInterest.push({ id: '', name: '' });

                          setCountriesOfInterestList(nextCountriesOfInterest);
                        }}
                        tabIndex={-1}
                      >
                        <span>+</span> add one more Countries
                      </button>
                    )}
                  <div className={cn.separator} />
                </>
              )}
              {applicationScheme?.vatNumberActive && (
                <InfoRow title="VAT Number" id="vatNumber" lessPadding>
                  <Controller
                    control={control}
                    name="vatNumber"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <InputText
                        name="vatNumber"
                        placeholder="Enter VAT..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['vatNumber']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        error={errors?.vatNumber?.message}
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              <InfoRow
                title="Business description (optional)"
                id="businessDescription"
                className={cn.noFlex}
                lessPadding
              >
                <Controller
                  control={control}
                  name="businessDescription"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="businessDescription"
                      placeholder="Enter desription..."
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['businessDescription']);
                      }}
                      value={value}
                      className={`borderless ${cn.stepOneTextarea}`}
                      error={errors?.businessDescription?.message}
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                    />
                  )}
                />
              </InfoRow>
              {deliveryChannelsVisible && (
                <InfoRow
                  title={
                    applicationScheme?.deliveryChannelTitle ||
                    'Delivery channel'
                  }
                  id="deliveryChannelId"
                  lessPadding
                >
                  <Controller
                    control={control}
                    name="deliveryChannelId"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="deliveryChannelId"
                        placeholder="Select an option..."
                        list={deliveryChannelsCorporate}
                        value={value}
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['deliveryChannelId']);
                        }}
                        selectWrapperClassName={
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? cn.selectDisabled
                            : ''
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={errors?.deliveryChannelId?.message}
                        borderless
                        fullWidth
                      />
                    )}
                  />
                </InfoRow>
              )}
              {(applicationScheme?.amlCtfComplianceProgramActive ||
                applicationScheme?.expectedTypeOfClientsActive ||
                applicationScheme?.purposeOfAccountActive ||
                applicationScheme?.requiredCurrenciesCryptoActive) && (
                <>
                  <div className={cn.stepThreeCorporateBlockTitle}>
                    Business & Compliance Profile
                  </div>
                  {applicationScheme?.purposeOfAccountActive && (
                    <InfoRow
                      title="Purpose of account"
                      id="businessLicense"
                      lessPadding
                    >
                      <Controller
                        control={control}
                        name="purposeOfAccount"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Textarea
                            name="purposeOfAccount"
                            placeholder="Describe Purpose of account"
                            onChange={(e) => {
                              onChange(e);
                              clearErrors(['purposeOfAccount']);
                            }}
                            value={value}
                            className={`borderless ${cn.stepOneTextarea} ${cn.purposeOfAccount}`}
                            error={errors?.purposeOfAccount?.message}
                            tabIndex={
                              !isEditable &&
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                                ? -1
                                : 0
                            }
                            disabled={
                              !isEditable ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                          />
                        )}
                      />
                    </InfoRow>
                  )}
                  {applicationScheme?.expectedTypeOfClientsActive && (
                    <InfoRow
                      title="Expected types of clients"
                      id="expectedTypeOfClients"
                      lessPadding
                    >
                      <Controller
                        control={control}
                        name="expectedTypeOfClients"
                        defaultValue={[]}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="expectedTypeOfClients"
                            placeholder="Select types"
                            list={[
                              {
                                name: 'B2B',
                                value: 'B2B',
                              },
                              {
                                name: 'B2C',
                                value: 'B2C',
                              },
                            ]}
                            value={value}
                            size={SelectSize.SMALL}
                            onChange={(e) => {
                              onChange(e);
                              clearErrors(['expectedTypeOfClients']);
                            }}
                            mode={SelectType.MULTIPLE}
                            selectWrapperClassName={
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? cn.selectDisabled
                                : ''
                            }
                            disabled={
                              !isEditable ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            tabIndex={
                              !isEditable ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                                ? -1
                                : 0
                            }
                            error={errors?.expectedTypeOfClients?.message}
                            borderless
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                  )}
                  {applicationScheme?.requiredCurrenciesCryptoActive && (
                    <>
                      {requiredCurrenciesList?.map((item, index) => (
                        <RequiredCurrencies
                          title="Required currency"
                          // eslint-disable-next-line react/no-array-index-key
                          key={`requiredCurrenciesCryptoId-${item.name}-${index}`}
                          value={item}
                          setValue={setValue}
                          values={requiredCurrenciesList}
                          setValuesList={setRequiredCurrenciesList}
                          list={currencies}
                          index={index}
                          clearErrors={clearErrors}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          error={
                            errors.requiredCurrenciesCryptoId?.message ||
                            errors.requiredCurrenciesCryptoId?.message
                          }
                        />
                      ))}
                      {isEditable &&
                        status !== NewApplicationStatus.ACCEPTED &&
                        status !== NewApplicationStatus.DECLINED && (
                          <button
                            type="button"
                            className={cn.stepOneValueButton}
                            onClick={() => {
                              const nextRequiredCurrencies = (
                                [] as { id: string; name: string }[]
                              ).concat(requiredCurrenciesList);

                              nextRequiredCurrencies.push({ id: '', name: '' });

                              setRequiredCurrenciesList(nextRequiredCurrencies);
                            }}
                            tabIndex={-1}
                          >
                            <span>+</span> add one more Currencies
                          </button>
                        )}
                    </>
                  )}
                  {applicationScheme?.amlCtfComplianceProgramActive && (
                    <InfoRow
                      title="Do you have a AML/CTF Compliance Programm"
                      id="amlCtfComplianceProgram"
                      lessPadding
                    >
                      <Controller
                        control={control}
                        name="amlCtfComplianceProgram"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="amlCtfComplianceProgram"
                            placeholder="Select an option..."
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            size={SelectSize.SMALL}
                            onChange={(e) => {
                              onChange(e);
                              clearErrors(['amlCtfComplianceProgram']);
                            }}
                            selectWrapperClassName={
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? cn.selectDisabled
                                : ''
                            }
                            disabled={
                              !isEditable ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            tabIndex={
                              !isEditable &&
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                                ? -1
                                : 0
                            }
                            error={errors?.amlCtfComplianceProgram?.message}
                            fullWidth
                            borderless
                          />
                        )}
                      />
                    </InfoRow>
                  )}
                </>
              )}
            </>
          </StepPartBlock>
        </div>
      </StepOneBlockWrapper>
      {regulatedCompanyScheme && (
        <>
          <StepOneBlockWrapper title="Economic Profile" regulatedCompanyScheme>
            <div className={cn.stepBlockContent}>
              <PepQuestionnarie
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                status={status}
                isEditable={isEditable}
                questionnarieErrors={questionnarieErrors}
                questionnarieFields={questionnarieFields}
                countries={countries}
                relationshipToTheCustomerDictionary={
                  relationshipToTheCustomerDictionary
                }
                questionnarieSetValue={questionnarieSetValue}
              />
              <GroupOfCompanies
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                countries={countries}
                sourceOfWealth={sourceOfWealth}
                baseCurrency={baseCurrency}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
              />
              <BusinessActivities
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                busniessActivities={busniessActivities}
                customerMainCleints={customerMainCleints}
                salesChannels={salesChannels}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
                customerSourceOfFunds={customerSourceOfFunds}
                companyLegalName={companyLegalName}
              />
              <CustomerWealth
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
                annualIncomes={annualIncomes}
                totalIncomeError={totalIncomeError}
                setTotalIncomeError={setTotalIncomeError}
                customerSourceOfWealth={customerSourceOfWealth}
                baseCurrency={baseCurrency}
              />
            </div>
          </StepOneBlockWrapper>
          <StepOneBlockWrapper
            title="Account & Expected Activity"
            regulatedCompanyScheme
          >
            <div className={cn.stepBlockContent}>
              <StepPartBlock title="Purpose for establishing business relationship">
                <InfoRow
                  title="Type of the account"
                  id="accountTypeIds"
                  style={{
                    position: 'relative',
                    zIndex: 7,
                  }}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="accountTypeIds"
                    defaultValue={[]}
                    render={({ field: { onChange, value, name } }) => (
                      <Select
                        name={name}
                        placeholder="Select an option..."
                        list={
                          typeOfAccounts?.map((item) => ({
                            name: item.value,
                            value: item.id,
                          })) || []
                        }
                        value={value}
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['accountTypeIds']);
                        }}
                        selectWrapperClassName={
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? cn.selectDisabled
                            : ''
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.accountTypeIds?.message}
                        mode={SelectType.MULTIPLE}
                        fullWidth
                        borderless
                      />
                    )}
                  />
                </InfoRow>
                <div className={cn.accountsCurrenciesList}>
                  {questionnarieErrors?.questionnairesAccountsCurrencies
                    ?.message && (
                    <div className={cn.questionnarieError}>
                      {
                        questionnarieErrors?.questionnairesAccountsCurrencies
                          ?.message
                      }
                    </div>
                  )}
                  {!accountsCurrenciesIsLoading &&
                    !accountsCurrenciesIsFetching && (
                      <>
                        {accountsCurrenciesValues?.map((item, index) => (
                          <AccountCurrency
                            key={item.id}
                            accountCurrency={item}
                            setAccountsCurrenciesValues={
                              setAccountsCurrenciesValues
                            }
                            currencies={currencies}
                            index={index}
                            isEditable={isEditable}
                            status={status}
                            accountsCurrencies={accountsCurrenciesValues}
                            onDeleteAccountCurrencies={
                              onDeleteAccountCurrencies
                            }
                            selectedAccountsCurrencyId={
                              selectedAccountsCurrencyId
                            }
                            deleteAccountsCurrenciesInProgress={
                              deleteAccountsCurrenciesInProgress
                            }
                            setAccountsFlowsValues={setAccountsFlowsValues}
                            accountsFlowsValues={accountsFlowsValues}
                          />
                        ))}
                        {currencies &&
                          currencies?.filter((item) =>
                            accountsCurrenciesValues?.every(
                              (accountCurrency) =>
                                item.value !== accountCurrency.currencyId,
                            ),
                          )?.length > 0 && (
                            <ButtonSecondary
                              text="Add currency"
                              icon={<IconExpand />}
                              className={`button-white ${cn.addAccountCurrencyButton}`}
                              size={ButtonSize.SMALL}
                              onClick={() => {
                                let nextAccountsCurrencies: AccountsCurrenciesFormValues[] =
                                  [];

                                if (accountsCurrenciesValues) {
                                  nextAccountsCurrencies = (
                                    [] as AccountsCurrenciesFormValues[]
                                  ).concat(accountsCurrenciesValues);
                                }

                                nextAccountsCurrencies.push({
                                  id: `accounts-currency-${uuidv4()}`,
                                  currencyId: '',
                                  plannedTransactionNature: '',
                                  accountOpeningPurpose: '',
                                });

                                setAccountsCurrenciesValues(
                                  nextAccountsCurrencies,
                                );
                              }}
                            />
                          )}
                      </>
                    )}
                  {(accountsCurrenciesIsLoading ||
                    accountsCurrenciesIsFetching) && (
                    <div className={cn.accountsCurrenciesLoader}>
                      <IconLoader />
                    </div>
                  )}
                </div>
              </StepPartBlock>

              <StepPartBlock title="Expected flows on own account">
                {!accountsFlowsIsLoading &&
                  !accountsFlowsIsFetching &&
                  accountsFlowsValues &&
                  accountsFlowsValues?.length > 0 && (
                    <div className={cn.expectedFlowsHint}>
                      Currencies selected in the&nbsp;
                      <strong>
                        &quot;Purpose for establishing business
                        relationship&quot;
                      </strong>
                      &nbsp; section
                    </div>
                  )}

                <div className={cn.accountsCurrenciesList}>
                  {questionnarieErrors?.questionnairesAccountsFlows
                    ?.message && (
                    <div className={cn.questionnarieError}>
                      {
                        questionnarieErrors?.questionnairesAccountsFlows
                          ?.message
                      }
                    </div>
                  )}
                  {(accountsFlowsIsLoading || accountsFlowsIsFetching) && (
                    <div className={cn.accountsCurrenciesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {!accountsFlowsValues?.length && (
                    <div className={cn.emptyAccountsFlows}>
                      Add currencies in&nbsp;
                      <strong>
                        &quot;Purpose for establishing business
                        relationship&quot;
                      </strong>
                    </div>
                  )}
                  {!accountsFlowsIsLoading &&
                    !accountsFlowsIsFetching &&
                    Object.keys(groupedAccountsFlows)?.map((group) =>
                      groupedAccountsFlows[group]?.map((flow) => (
                        <AccountFlow
                          key={flow.id}
                          accountFlow={flow}
                          accountsFlows={accountsFlowsValues}
                          setAccountsFlowsValues={setAccountsFlowsValues}
                          currencies={currencies}
                          isEditable={isEditable}
                          status={status}
                          currenciesList={currenciesList}
                        />
                      )),
                    )}
                </div>
              </StepPartBlock>
              <StepPartBlock title="Expected origin of funds">
                <div className={cn.accountsCurrenciesList}>
                  {(expectedFundsOriginsIsLoading ||
                    expectedFundsOriginsIsFetching) && (
                    <div className={cn.accountsCurrenciesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {!accountsFlowsValues?.length && (
                    <div className={cn.emptyAccountsFlows}>
                      Add currencies in&nbsp;
                      <strong>
                        &quot;Purpose for establishing business
                        relationship&quot;
                      </strong>
                    </div>
                  )}
                  {questionnarieErrors?.questionnairesExpectedFundsOrigins
                    ?.message && (
                    <div className={cn.questionnarieError}>
                      {
                        questionnarieErrors?.questionnairesExpectedFundsOrigins
                          ?.message
                      }
                    </div>
                  )}
                  {accountsFlowsValues &&
                    accountsFlowsValues?.length > 0 &&
                    !expectedFundsOriginsIsLoading &&
                    !expectedFundsOriginsIsFetching && (
                      <>
                        <ExpectedFundsOrigins
                          status={status}
                          isEditable={isEditable}
                          currencies={
                            accountsCurrenciesValues?.map((item) => ({
                              name: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.name as string,
                              suffix: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.suffix as string,
                              value: item.currencyId,
                            })) || []
                          }
                          deleteExpectedFundsOriginsInProgress={
                            deleteExpectedFundsOriginsInProgress
                          }
                          onDeleteExpectedFundsOrigins={
                            onDeleteExpectedFundsOrigins
                          }
                          selectedExpectedFundsOriginsId={
                            selectedExpectedFundsOriginsId
                          }
                          expectedFundsOriginsValues={
                            incomingExpectedFundsOfOrigins
                          }
                          expectedFundsOriginsValuesList={
                            expectedFundsOriginsValues
                          }
                          setExpectedFundsOriginsValues={
                            setExpectedFundsOriginsValues
                          }
                          countries={countries}
                          type="INCOMING"
                        />
                        <ExpectedFundsOrigins
                          status={status}
                          isEditable={isEditable}
                          currencies={
                            accountsCurrenciesValues?.map((item) => ({
                              name: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.name as string,
                              suffix: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.suffix as string,
                              value: item.currencyId,
                            })) || []
                          }
                          deleteExpectedFundsOriginsInProgress={
                            deleteExpectedFundsOriginsInProgress
                          }
                          onDeleteExpectedFundsOrigins={
                            onDeleteExpectedFundsOrigins
                          }
                          selectedExpectedFundsOriginsId={
                            selectedExpectedFundsOriginsId
                          }
                          expectedFundsOriginsValues={
                            outgoingExpectedFundsOfOrigins
                          }
                          expectedFundsOriginsValuesList={
                            expectedFundsOriginsValues
                          }
                          setExpectedFundsOriginsValues={
                            setExpectedFundsOriginsValues
                          }
                          countries={countries}
                          type="OUTGOING"
                        />
                      </>
                    )}
                </div>
              </StepPartBlock>
            </div>
          </StepOneBlockWrapper>
        </>
      )}
    </div>
  );
};

export default NewApplicationCorporateInfoStepOne;
